import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import { useClickAway } from 'react-use'
import { ChevronDown } from '@styled-icons/boxicons-regular'
import { gsap, Power3 } from 'gsap'

import { MonoP, InternalLink } from 'src/components/shared/text'
import { SecondaryBtn } from 'src/components/shared/buttons'
import { media } from 'src/utils/style-helper'

const Container = styled.div`
  width: 100%;
  height: fit-content;
  z-index: 100;
  padding: 0 10px;
`

const DropdownMenu = styled.div`
  width: 100%;
  border-bottom-right-radius: 25px;
  border-bottom-left-radius: 25px;
  border: 1px solid ${(props) => props.theme.colors.slate};
  background-color: white;
  padding: 16px 10px 10px 10px;
  margin-top: -2px;
  height: 0;
  display: none;
`

const TextButton = styled(MonoP)`
  font-size: 16px;
  color: ${(props) => props.theme.colors.slate};
  padding: 16px 20px;
  text-align: center;
`

const StyledButton = styled(SecondaryBtn)`
  width: 100%;
  background: ${(props) =>
    props.open ? props.theme.colors.navy : 'transparent'};
  color: ${(props) =>
    props.open ? props.theme.colors.lightText : props.theme.colors.slate};
  border: 1px solid
    ${(props) => (props.open ? 'transparent' : props.theme.colors.slate)};
  line-height: 1 !important;
  border-bottom-right-radius: ${(props) => (props.open ? 0 : 60)}px;
  border-bottom-left-radius: ${(props) => (props.open ? 0 : 60)}px;
  border-top-right-radius: ${(props) => (props.open ? 20 : 60)}px;
  border-top-left-radius: ${(props) => (props.open ? 20 : 60)}px;
  transition: 0.1s all ease-in-out;
  ${media.mobile`
    padding: 9px 20px 7px 20px;
  `} ${media.desktop`
    padding: 9px 20px 7px 20px;
  `} 
  & p {
    font-size: 16px;
  }
`

const CategoryIcon = styled(ChevronDown)`
  transform: ${(props) => (props.open ? 'rotate(180deg)' : 'none')};
  transition: 200ms transform ease-in-out;
  height: 28px;
  width: 20px;
  margin-top: -4px;
`

const DropdownNav = ({ categories }) => {
  const [open, setOpen] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)

  const handleOpen = () => {
    if (!isAnimating) {
      setIsAnimating(true)
      gsap.to('.DropdownNav_DropdownMenu', {
        display: 'block',
        padding: '16px 10px 10px 10px',
        height: 325,
        duration: 0.2,
        ease: Power3.easeOut,
      })
      setOpen(true)
      setIsAnimating(false)
    }
  }

  const handleClose = () => {
    if (!isAnimating) {
      setIsAnimating(true)
      gsap.to('.DropdownNav_DropdownMenu', {
        height: 0,
        padding: 0,
        ease: Power3.easeOut,
        duration: 0.25,
      })
      gsap.to('.DropdownNav_DropdownMenu', {
        display: 'none',
        delay: 0.1,
        duration: 0.1,
      })
      setOpen(false)
      setIsAnimating(false)
    }
  }

  const handleBtnClick = () => {
    if (!open) handleOpen()
    else handleClose()
  }

  const ref = useRef(null)
  useClickAway(ref, handleClose)

  if (typeof window !== 'undefined') {
    window.onscroll = () => {
      handleClose()
      setOpen(false)
    }
  }

  return (
    <Container ref={ref}>
      <StyledButton onClick={handleBtnClick} open={open}>
        <MonoP>
          Categories
          <CategoryIcon open={open} />
        </MonoP>
      </StyledButton>
      <DropdownMenu className="DropdownNav_DropdownMenu" onClick={handleClose}>
        {categories.map(({ label, slug }) => (
          <InternalLink key={label} to={slug} name={label}>
            <TextButton>{label}</TextButton>
          </InternalLink>
        ))}
      </DropdownMenu>
    </Container>
  )
}

export default DropdownNav
